import { useEffect, useState } from "react";
import { PackageCheck, Loader2, Wrench, Truck, FileText, Printer, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { useAutosaveOnBlur, AutosaveBadge } from "./useAutosaveOnBlur";
import { DispoBackButton } from "./DispoBackButton";
import { FeatureGuideTriangle } from "./FeatureGuideTriangle";

// Checkliste — Keys müssen mit handover_protocol_pdf.HANDOVER_ITEMS übereinstimmen
const HANDOVER_ITEMS = [
    ["keys", "Schlüssel"],
    ["zb1", "ZB I (Fahrzeugschein)"],
    ["zb2", "ZB II (Fahrzeugbrief)"],
    ["coc", "COC-Papier"],
    ["service_book", "Serviceheft"],
    ["manual", "Bedienungsanleitung"],
    ["tools", "Bordwerkzeug"],
    ["second_tyres", "Zweiter Reifensatz"],
    ["accessories", "Zubehör lt. Vertrag"],
    ["tuev_report", "HU/AU-Bericht"],
];

export function HandoverTuevModal({ listingId, hasInvoice, onBack, onClose, onSaved, onOpenTransport }) {
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/listings/${listingId}/dispo`;
    const [tuev, setTuev] = useState(null);
    const [wo, setWo] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [creatingWo, setCreatingWo] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        handover_date: new Date().toISOString().slice(0, 10),
        place: "", mileage_at_handover: "", fuel_level: "", notes: "",
        items: {},
    });
    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggle = (k) => setForm((f) => ({ ...f, items: { ...f.items, [k]: !f.items[k] } }));

    // Jun-2026 (User-Wunsch) — Auto-Speichern beim Verlassen eines Feldes.
    const autosave = useAutosaveOnBlur(async () => {
        if (!form.handover_date) return false;
        try {
            await api.post(`/listings/${listingId}/dispo/handover`, { ...form, mileage_at_handover: Number(form.mileage_at_handover) || 0 });
            await onSaved?.();
            return true;
        } catch { return false; }
    }, () => JSON.stringify(form), { enabled: loaded });

    useEffect(() => {
        let alive = true;
        Promise.all([
            api.get(`/listings/${listingId}/dispo/tuev-check`).then((r) => r.data).catch(() => null),
            api.get(`/listings/${listingId}/dispo/workshop-order`).then((r) => r.data).catch(() => null),
            api.get(`/listings/${listingId}/dispo/handover`).then((r) => r.data).catch(() => null),
        ]).then(([t, w, h]) => {
            if (!alive) return;
            setTuev(t);
            setWo(w && w.work_order_id ? w : null);
            if (h && h.handover_date) {
                setForm({
                    handover_date: h.handover_date || new Date().toISOString().slice(0, 10),
                    place: h.place || "", mileage_at_handover: h.mileage_at_handover ?? "",
                    fuel_level: h.fuel_level || "", notes: h.notes || "", items: h.items || {},
                });
            }
        }).finally(() => { if (alive) setLoaded(true); });
        return () => { alive = false; };
    }, [listingId]);

    const createWorkshopOrder = async () => {
        setCreatingWo(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/workshop-order`);
            setWo(r.data.dispo_workshop_order);
            toast.success(`Werkstattauftrag ${r.data.dispo_workshop_order.order_number} angelegt`);
            await onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Werkstattauftrag fehlgeschlagen");
        } finally { setCreatingWo(false); }
    };

    const saveHandover = async () => {
        setSaving(true);
        try {
            await api.post(`/listings/${listingId}/dispo/handover`, {
                ...form, mileage_at_handover: Number(form.mileage_at_handover) || 0,
            });
            toast.success("Übergabeprotokoll gespeichert");
            await onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dispo-modal-handover" onBlur={autosave.onBlur}>
                <FeatureGuideTriangle guideKey="handover" />
                <DialogHeader><DialogTitle className="flex items-center gap-2"><PackageCheck className="w-5 h-5 text-orange-600" /> Auslieferung</DialogTitle></DialogHeader>
                <AutosaveBadge status={autosave.status} savedAt={autosave.savedAt} />

                {/* TÜV-Check Banner */}
                {!loaded && (
                    <div className="flex items-center gap-2 text-sm text-gray-400" data-testid="dispo-modal-handover-loading">
                        <Loader2 className="w-4 h-4 animate-spin" /> TÜV-Status &amp; Übergabe werden geladen…
                    </div>
                )}
                {loaded && tuev && (
                    tuev.tuev_neu ? (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-xs text-emerald-800 flex items-center gap-2" data-testid="dispo-tuev-ok">
                            <CheckCircle2 className="w-4 h-4" /> Neue HU/TÜV bei Verkauf — kein Werkstattauftrag nötig.
                        </div>
                    ) : tuev.needs_action ? (
                        <div className="bg-amber-50 border-2 border-amber-300 rounded-sm p-3" data-testid="dispo-tuev-warning">
                            <div className="flex items-center gap-1.5 font-bold text-sm text-amber-900 mb-1">
                                <AlertTriangle className="w-4 h-4" /> HU/TÜV läuft bald ab
                            </div>
                            <div className="text-xs text-amber-800 mb-2">
                                Gültig bis <b>{tuev.tuev_until}</b> ({tuev.expired ? "abgelaufen" : `noch ${tuev.months_left} Monate`}).
                                Empfehlung: Werkstattauftrag (HU/TÜV) vor Übergabe + ggf. Transportauftrag.
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {wo ? (
                                    <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700" data-testid="dispo-wo-done">
                                        <CheckCircle2 className="w-4 h-4" /> Werkstattauftrag {wo.order_number} angelegt
                                    </span>
                                ) : (
                                    <Button type="button" size="sm" onClick={createWorkshopOrder} disabled={creatingWo} className="bg-orange-500 hover:bg-orange-600 text-white h-8 text-xs" data-testid="dispo-create-workshop-order">
                                        {creatingWo ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wrench className="w-3.5 h-3.5 mr-1" /> Werkstattauftrag (HU/TÜV) anlegen</>}
                                    </Button>
                                )}
                                <Button type="button" size="sm" variant="outline" onClick={() => { onClose(); onOpenTransport?.(); }} className="border-2 h-8 text-xs" data-testid="dispo-open-transport-from-tuev">
                                    <Truck className="w-3.5 h-3.5 mr-1" /> Transportauftrag
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-gray-50 border rounded-sm p-3 text-xs text-gray-500 flex items-center gap-2" data-testid="dispo-tuev-fine">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" /> HU/TÜV ausreichend — Details unter „Zubehör &amp; Dienstleistungen".
                        </div>
                    )
                )}

                {/* Rechnung */}
                <div className="flex items-center gap-2 flex-wrap" data-testid="dispo-handover-invoice">
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-700">Rechnung:</span>
                    {hasInvoice ? (
                        <a href={`${base}/invoice.pdf?ts=${Date.now()}`} target="_blank" rel="noopener noreferrer" data-testid="dispo-invoice-pdf-link">
                            <Button type="button" variant="outline" size="sm" className="border-2 h-8 text-xs"><FileText className="w-3.5 h-3.5 mr-1" /> Rechnung (PDF)</Button>
                        </a>
                    ) : (
                        <span className="text-xs text-gray-400">Noch keine Rechnung — über „Rechnung schreiben" erstellen.</span>
                    )}
                </div>

                {/* Übergabeprotokoll */}
                <div className="border-t pt-3">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Übergabeprotokoll</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <div><Label className="text-xs">Übergabedatum</Label><Input type="date" value={form.handover_date} onChange={(e) => upd("handover_date", e.target.value)} data-testid="dispo-handover-date" /></div>
                        <div><Label className="text-xs">Ort</Label><Input value={form.place} onChange={(e) => upd("place", e.target.value)} data-testid="dispo-handover-place" /></div>
                        <div><Label className="text-xs">km-Stand bei Übergabe</Label><Input type="number" min="0" value={form.mileage_at_handover} onChange={(e) => upd("mileage_at_handover", e.target.value)} className="font-mono-num" data-testid="dispo-handover-mileage" /></div>
                        <div className="col-span-2 sm:col-span-1"><Label className="text-xs">Tankfüllung / Ladung</Label><Input value={form.fuel_level} onChange={(e) => upd("fuel_level", e.target.value)} placeholder="z. B. voll / 80 %" data-testid="dispo-handover-fuel" /></div>
                    </div>

                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-3 block">Ausgehändigte Gegenstände / Dokumente</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 mt-1" data-testid="dispo-handover-items">
                        {HANDOVER_ITEMS.map(([key, label]) => (
                            <label key={key} className="flex items-center gap-2 text-xs cursor-pointer py-0.5">
                                <input type="checkbox" checked={Boolean(form.items[key])} onChange={() => toggle(key)} className="accent-orange-500" data-testid={`dispo-handover-item-${key}`} />
                                {label}
                            </label>
                        ))}
                    </div>

                    <div className="mt-3"><Label className="text-xs">Mängel / Bemerkungen</Label><Textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} data-testid="dispo-handover-notes" /></div>
                </div>

                <DialogFooter className="gap-2">
                    <DispoBackButton onBack={onBack} />
                    <a href={`${base}/handover.pdf?ts=${Date.now()}`} target="_blank" rel="noopener noreferrer" data-testid="dispo-handover-pdf-link">
                        <Button type="button" variant="outline" className="border-2"><Printer className="w-4 h-4 mr-1" /> Protokoll (PDF)</Button>
                    </a>
                    <Button type="button" variant="outline" onClick={onClose}>Schließen</Button>
                    <Button type="button" onClick={saveHandover} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="dispo-handover-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Protokoll speichern"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
